import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import { useArchiveNotifications, useMarkNotificationsRead, useNotificationList, useUnreadNotificationCount } from '../../application';
import type { NotificationEntry } from '../../domain/types';
import { NotificationItem } from './NotificationItem';

export function NotificationList() {
  const [page, setPage] = useState(1);
  const list = useNotificationList({ page, perPage: 10 });
  const markRead = useMarkNotificationsRead();
  const archive = useArchiveNotifications();
  const unreadCount = useUnreadNotificationCount();
  const pending = markRead.isPending || archive.isPending;
  useEffect(() => {
    if (list.data) setPage(current => Math.min(current, Math.max(1, list.data.pages)));
  }, [list.data]);
  const retry = { '&:focus-visible': { outline: (theme: Theme) => `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 } };
  const renderItem = (item: NotificationEntry) => (
    <NotificationItem
      key={item.id}
      notification={item}
      onMarkRead={() => markRead.mutate({ notificationIds: [item.notificationId] })}
      onArchive={() => archive.mutate({ notificationIds: [item.notificationId] })}
      isActionPending={pending}
      actionError={markRead.isError || archive.isError ? 'Action failed. Retry below.' : null}
    />
  );
  const retryAction = () => {
    if (markRead.isError) markRead.mutate(markRead.variables);
    else if (archive.isError) archive.mutate(archive.variables);
  };
  const failure = markRead.error ?? archive.error;
  return (
    <Box sx={{ width: 'min(420px, calc(100vw - 24px))', maxHeight: 'min(620px, calc(100vh - 88px))', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Notifications
        </Typography>
        <Button size="small" disabled={pending || !unreadCount.data} onClick={() => markRead.mutate({})} sx={retry}>
          Mark all read
        </Button>
      </Box>
      <Box sx={{ overflowY: 'auto', minHeight: 80 }}>
        {(list.isLoading || (list.isFetching && !list.data)) && (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} aria-label="Loading notifications" />
          </Box>
        )}
        {list.isError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void list.refetch()} sx={retry}>
                Retry
              </Button>
            }
          >
            Could not load notifications: {list.error instanceof Error ? list.error.message : 'Try again.'}
          </Alert>
        )}
        {list.data?.items.map(renderItem)}
        {list.data?.items.length === 0 && <Typography sx={{ p: 3, textAlign: 'center' }}>No notifications</Typography>}
      </Box>
      {(markRead.isError || archive.isError) && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={retryAction} sx={retry}>
              Retry
            </Button>
          }
        >
          Action failed: {failure instanceof Error ? failure.message : 'Try again.'}
        </Alert>
      )}
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption">{list.data ? `${String(list.data.total)} total` : ''}</Typography>
        <Box>
          <Button size="small" disabled={page <= 1} onClick={() => setPage(value => value - 1)} sx={retry}>
            Previous
          </Button>
          <Typography component="span" variant="caption" sx={{ mx: 1 }}>
            {list.data ? `${String(list.data.page)} / ${String(Math.max(1, list.data.pages))}` : ''}
          </Typography>
          <Button size="small" disabled={!list.data || page >= list.data.pages} onClick={() => setPage(value => value + 1)} sx={retry}>
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
